import { CssBaseline } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import isEmpty from 'lodash/isEmpty';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRoutes } from 'react-router-dom';
import { AzureWebPubSubChatContext } from 'src/contexts/AzureWebPubSubChatContext';
import router from 'src/router';

import AppInit from './components/AppInit';
import SnackbarAlert from './components/NotiStack';
import GlobalSessionManager from './components/tailwind-components/GlobalSessionManager';
import GlobalUploadManager from './components/UploadWidget/GlobalUploadManager';
import LicenseExpiry from './content/LicenseExpiry';
import MinimizedRecording from './content/MiniExperts/SmartMeet/components/Recorder/MinimizedRecorder';
import { SessionWidgetProvider } from './context/SessionWidgetContext';
import { UploadWidgetProvider } from './context/UploadWidgetContext';
import useAnalytics from './hooks/useAnalytics';
import useGlobalData from './hooks/useGlobalData';
import usePreventTabClose from './hooks/usePreventTabClose';
import { getKccToken } from './keycloak';
import { authSignIn } from './redux/slices/auth';
import { useDispatch, useSelector } from './redux/store';
import ThemeProvider from './theme/ThemeProvider';
import { PAGE_VIEWED_EVENT } from './utils/analyticsUtils';
import { checkCreditsLimit, isPublicRoute } from './utils/utils';

function App() {
  const { isLangInitialized, userAppPermissions, areModelsInitialised } =
    useSelector((state) => state.data);

  const dispatch = useDispatch();
  const content = useRoutes(router(userAppPermissions));
  const { isRecording, isMinimized } = useSelector((state) => state.smartMeet);
  const collectAnalytics = useAnalytics();
  const preventTabClose = isRecording;

  usePreventTabClose(preventTabClose);

  const { initialize } = useGlobalData();
  const { isAuthorized, user, isLicenseExpired } = useSelector(
    (state) => state.auth
  );
  const { isOpen } = useSelector((state) => state.snackbar);
  const { t } = useTranslation();
  const kcc_token: any = getKccToken();
  const isSessionUpdating = useRef(false);
  const location = useLocation();
  const { connectionId, reconnectPubSub } = useContext(
    AzureWebPubSubChatContext
  );
  const wasConnected = useRef(false);
  const reconnecting = useRef(false);

  useEffect(() => {
    if (connectionId) {
      wasConnected.current = true;
      reconnecting.current = false;
      return;
    }

    if (!connectionId && wasConnected.current && !reconnecting.current) {
      reconnecting.current = true;
      void reconnectPubSub();
    }
  }, [connectionId]);

  useEffect(() => {
    if (!isSessionUpdating.current) {
      if (kcc_token === 'true') {
        dispatch(authSignIn());
        isSessionUpdating.current = true;
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthorized && user) {
      initialize();
    }
  }, [user]);

  useEffect(() => {
    collectAnalytics(PAGE_VIEWED_EVENT);
  }, [location.pathname]);

  const showMinimizedRecorder = isRecording && isMinimized;

  return (
    <UploadWidgetProvider>
      <SessionWidgetProvider>
        <ThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            {showMinimizedRecorder ? <MinimizedRecording /> : null}
            <GlobalUploadManager />
            <GlobalSessionManager />
            {isLicenseExpired ? (
              <LicenseExpiry />
            ) : (
              <>
                {(isAuthorized &&
                  user &&
                  isLangInitialized &&
                  areModelsInitialised &&
                  !isEmpty(userAppPermissions)) ||
                isPublicRoute() ? (
                  content
                ) : (
                  <AppInit />
                )}
                {isOpen ? <SnackbarAlert /> : null}
              </>
            )}
          </LocalizationProvider>
        </ThemeProvider>
      </SessionWidgetProvider>
    </UploadWidgetProvider>
  );
}
export default App;
